// NOMBASE - Customizable sidebar configuration
// Each tenant keeps their own ordered list of sidebar items, minus the
// platform-fixed ones (Dashboard, Ask Noma, Apps, Profile). The list is
// persisted to localStorage so it survives refresh and login/logout. Max
// length is capped to the role's *default* item count — per product
// requirement the current sidebar size is the ceiling.
//
// On first use (no localStorage yet), the hook seeds from the tenant-wide
// default if one has been set by a tenant admin. New seats therefore
// inherit a curated layout without any extra UX.
import { useState, useCallback, useEffect, useMemo } from 'react';

const STORAGE_PREFIX = 'nombase_sidebar_v2';

/**
 * @param {string} role        e.g. 'brand' | 'supplier' | 'comfg'
 * @param {string} tenantType  e.g. 'raw_ingredient_supplier'
 * @param {Array<{id,label,href,iconName}>} defaultItems
 * @param {Array<{id,label,href,iconName}>=} tenantDefault  Admin-set team default
 */
export function useSidebarConfig(role, tenantType, defaultItems, tenantDefault) {
  const storageKey = `${STORAGE_PREFIX}_${role}_${tenantType || 'x'}`;
  const maxItems = defaultItems.length;

  // Set of ids the user is NOT allowed to remove (e.g. brand
  // Home/Network/Product/Plan/Buy). Reorder is still allowed.
  // Derived from the static defaultItems so the hook stays a pure
  // function of its inputs.
  const coreIds = useMemo(
    () => new Set(
      (defaultItems || [])
        .filter(d => d && d.id && d.core === true)
        .map(d => d.id),
    ),
    [defaultItems],
  );

  // Migrations:
  //  - Legacy plan/production/procurement → single `operations` (Phase ✕).
  //  - `operations` → split into `plan` + `buy` (Operations v2 Phase 4).
  //  - Hard-removed: `rfq`, `development`.
  const migrate = useCallback((arr) => {
    if (!Array.isArray(arr)) return arr;
    const planDefault = defaultItems.find(d => d.id === 'plan');
    const buyDefault = defaultItems.find(d => d.id === 'buy');
    const operationsDefault = defaultItems.find(d => d.id === 'operations');
    const LEGACY_OPS_PARTS = new Set(['production', 'procurement']);
    // `autopilot` was a rail nav item briefly; it's now folded into the
    // Autopilot toggle control, so strip any stale persisted entry (otherwise
    // it occupies a slot and blocks core items like Documents from restoring).
    const REMOVED = new Set(['rfq', 'development', 'autopilot']);
    const out = [];
    const have = new Set();
    const push = (item) => {
      if (!item || !item.id || have.has(item.id)) return;
      have.add(item.id);
      out.push(item);
    };
    for (const it of arr) {
      if (!it) continue;
      if (REMOVED.has(it.id)) continue;
      if (it.id === 'operations') {
        if (planDefault) push(planDefault);
        if (buyDefault) push(buyDefault);
        continue;
      }
      if (LEGACY_OPS_PARTS.has(it.id)) {
        // Old plan/production/procurement IDs — collapse to plan + buy.
        if (planDefault) push(planDefault);
        if (buyDefault) push(buyDefault);
        // If neither is in defaults (supplier role), fall back to operations.
        if (!planDefault && !buyDefault && operationsDefault) push(operationsDefault);
        continue;
      }
      // Re-sync a persisted entry to the CURRENT default fields by id, so
      // renames (e.g. href /product → /products) and icon changes propagate to
      // saved layouts instead of rendering a stale link that never matches the
      // active route. Keeps the user's order; only refreshes the item's shape.
      const current = defaultItems.find(d => d.id === it.id);
      push(current || it);
    }
    // Auto-restore any default item that is supposed to be in every
    // user's sidebar but is currently missing — covers two cases:
    //   1. The user removed it via the × button (recoverable when we
    //      treat it as a "core" id).
    //   2. We added a new id (e.g. `buy` in Operations v2) after they
    //      already had a saved layout pinned without it.
    // Only the explicitly-listed core items auto-return.
    const CORE_AUTO_RESTORE = new Set(['plan', 'buy', 'network', 'product']);
    for (const d of defaultItems) {
      if (CORE_AUTO_RESTORE.has(d.id) && !have.has(d.id)) push(d);
    }
    return out;
  }, [defaultItems]);

  // Hydrate from localStorage; if empty, fall through to the tenant-wide
  // default (admin-pinned), and finally to the built-in defaults. We also
  // sanitise entries to guarantee the minimum shape (id/label/href/iconName).
  const [items, setItems] = useState(() => {
    const clean = (arr) => (Array.isArray(arr) ? arr : [])
      .filter(x => x && x.id && x.label && x.href && x.iconName)
      .slice(0, maxItems);
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = clean(migrate(JSON.parse(raw)));
        if (parsed.length > 0) return parsed;
      }
    } catch { /* fallthrough */ }
    // No personal config yet — inherit the tenant default, else built-in.
    const team = clean(migrate(tenantDefault));
    if (team.length > 0) return team;
    return defaultItems;
  });

  // When the tenant default arrives AFTER mount (async fetch) and the user
  // hasn't personalised yet, apply it silently. "Not personalised" means
  // either LS is empty OR LS still matches the built-in defaults exactly —
  // this avoids trampling a user who already customised, while still
  // inheriting the team layout for fresh seats.
  useEffect(() => {
    if (!Array.isArray(tenantDefault) || tenantDefault.length === 0) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const stored = JSON.parse(raw);
        if (Array.isArray(stored) && stored.length > 0) {
          const defaultIds = defaultItems.map(i => i.id).join('|');
          const storedIds = stored.map(i => i?.id).join('|');
          // If stored matches built-in defaults, the user is on their very
          // first session — go ahead and apply the team default.
          if (storedIds !== defaultIds) return;
        }
      }
    } catch { /* noop */ }
    const clean = migrate(tenantDefault)
      .filter(x => x && x.id && x.label && x.href && x.iconName)
      .slice(0, maxItems);
    if (clean.length > 0) setItems(clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantDefault, storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch { /* storage disabled */ }
  }, [storageKey, items]);

  // Self-heal: if any core item is missing from the current items
  // (e.g. the user removed Buy in a session that pre-dates the
  // un-removable-core patch and reloaded into the new code), push
  // it back into the list in its canonical position. Idempotent —
  // a no-op when every core id is already present. Bounded by
  // `maxItems`; when we're already at that ceiling we buy a slot by
  // evicting an *orphan* (see below) rather than giving up.
  useEffect(() => {
    if (coreIds.size === 0) return;
    setItems(prev => {
      const have = new Set(prev.map(x => x?.id));
      const missing = (defaultItems || []).filter(
        d => d && d.core && !have.has(d.id),
      );
      if (missing.length === 0) return prev;
      // An "orphan" is a persisted entry whose id is no longer in
      // defaultItems at all. The common way to get one is a nav surface
      // going dark behind a feature flag: AppShell filters it out of
      // navItems, so it drops out of defaultItems AND `maxItems` shrinks
      // by one, while the user's saved layout still carries it. Without
      // eviction the orphan pins the list at capacity and permanently
      // displaces a core item — e.g. flipping DEMAND_ENABLED back to
      // false would strand `demand` in saved layouts and silently drop
      // Documents off the rail, with no user-facing way to get it back.
      //
      // Core wins the tie: core items are un-removable by contract and
      // are the only navigation surface for their page, whereas an
      // orphan is either unreachable (flagged off) or re-addable from
      // the app launcher. We evict only when a core item actually needs
      // the slot, so a deliberately launcher-added item (Settings,
      // Search — ids that are legitimately absent from defaultItems) is
      // left alone as long as the rail is whole.
      //
      // Deliberately id-agnostic: keying this off DEMAND_ENABLED (or
      // adding `demand` to REMOVED above) would fix today's flag and
      // leave the next one to rediscover this bug.
      const defaultIds = new Set((defaultItems || []).map(d => d && d.id));
      const next = [...prev];
      let changed = false;
      // Tail-first: the end of the rail is the lowest-priority slot, so
      // dropping from there preserves the user's leading order.
      const evictOrphan = () => {
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (!defaultIds.has(next[i]?.id)) {
            next.splice(i, 1);
            return true;
          }
        }
        return false;
      };
      for (const m of missing) {
        // At capacity with nothing to evict — we never grow the rail
        // past the default item count, so stop here.
        if (next.length >= maxItems && !evictOrphan()) break;
        // Insert at the index the default layout suggests; fall
        // back to the end if the calculated slot is out of range.
        const canonicalIdx = defaultItems.findIndex(d => d.id === m.id);
        const insertAt = canonicalIdx >= 0 && canonicalIdx <= next.length
          ? canonicalIdx
          : next.length;
        next.splice(insertAt, 0, {
          id: m.id, label: m.label, href: m.href, iconName: m.iconName,
        });
        changed = true;
      }
      // Keep the reference stable when we couldn't actually heal
      // anything, so this stays a true no-op and can't spin renders.
      return changed ? next : prev;
    });
  }, [coreIds, defaultItems, maxItems]);

  const reorder = useCallback((fromIndex, toIndex) => {
    setItems(prev => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return prev;
      if (fromIndex >= prev.length) return prev;
      const arr = [...prev];
      const [moved] = arr.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(arr.length, toIndex));
      arr.splice(insertAt, 0, moved);
      return arr;
    });
  }, []);

  // `add()` lived here: it inserted an app dropped from the App launcher.
  // Both went together — every brand nav item is `core: true`, so nothing is
  // removable and the rail sits permanently at maxItems, which made the add
  // path unreachable by construction rather than merely unwired.

  const remove = useCallback((id) => {
    // Core items (e.g. brand Buy / Plan / Product / Network) are
    // intentionally un-removable — they're the entire navigation
    // surface for the page they point to. Reorder is still allowed
    // via `reorder()`.
    if (coreIds.has(id)) return;
    setItems(prev => prev.filter(x => x.id !== id));
  }, [coreIds]);

  const reset = useCallback(() => setItems(defaultItems), [defaultItems]);

  // Replace the full list with a sanitised payload. Used by "Apply team
  // default" so members can snap to the admin's curated order.
  const applyConfig = useCallback((incoming) => {
    const clean = (Array.isArray(incoming) ? incoming : [])
      .filter(x => x && x.id && x.label && x.href && x.iconName)
      .slice(0, maxItems);
    if (clean.length > 0) setItems(clean);
  }, [maxItems]);

  const isFull = items.length >= maxItems;
  const hasId = useCallback((id) => items.some(x => x.id === id), [items]);

  // Decorate items with the `core` flag at the boundary so consumers
  // (NavItem) can render the un-removable visual state without
  // having to consult defaultItems separately. `core` is intentionally
  // NOT persisted to localStorage (it's derived from defaultItems),
  // so we re-stamp it on every read.
  const itemsWithCore = useMemo(
    () => items.map(it => (
      coreIds.has(it?.id) ? { ...it, core: true } : it
    )),
    [items, coreIds],
  );

  return useMemo(
    () => ({ items: itemsWithCore, reorder, remove, reset, applyConfig, maxItems, isFull, hasId }),
    [itemsWithCore, reorder, remove, reset, applyConfig, maxItems, isFull, hasId]
  );
}
